import { resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  MedicationRequest,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { formatInTimeZone } from 'date-fns-tz'
import { DEFAULT_CLINIC_TIME_ZONE } from '#src/addressTimezone.ts'
import { MEDICATION_REFILL_TASK_ID_SYSTEM } from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import { TASK_TYPES } from '#src/models/tasks/constants.ts'
import { StandardTask } from '#src/models/tasks/standard/standardTask.ts'
import { Role } from '#src/roles.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export interface MedicationRefillMed {
  medicationRequestId: string
  display?: string
}

export interface MedicationRefillTaskParams {
  participant: Reference<Patient>
  cutoff: Date
  dueOn: Date | string
  owner?: Reference<Practitioner>
  title?: string
  description?: string
  medications?: readonly MedicationRefillMed[]
}

// One task per participant per cutoff. The identifier is `participantId|cutoffDate`
// (see `byParticipantAndCutoff`), carried as `ifNoneExist` on the conditional
// create so re-running against the same participant/cutoff is idempotent.
// A rerun that finds an open task adds any newly in-horizon medications to its
// `basedOn` and refreshes `title` in the same write; it never changes dueOn or
// state, and never reopens or auto-completes a task a human has already closed.
export class MedicationRefillTask extends StandardTask {
  static defaultRoles = [Role.PCP]
  static defaultTitle = 'Medication refill'

  static descriptionForCutoff(cutoff: Date): string {
    const day = formatInTimeZone(
      cutoff,
      DEFAULT_CLINIC_TIME_ZONE,
      'MMM d, yyyy'
    )
    return `Review and refill (if necessary) before ${day} cutoff.`
  }

  static basedOnMedications(
    meds: readonly MedicationRefillMed[]
  ): Reference<MedicationRequest>[] {
    return meds.map((med) => ({
      reference: `MedicationRequest/${med.medicationRequestId}`,
      display: med.display,
    }))
  }

  constructor({
    participant,
    cutoff,
    dueOn,
    owner,
    title,
    description,
    medications,
  }: MedicationRefillTaskParams) {
    super({
      code: MedicationRefillTask.code,
      focus: participant,
      for: participant,
      owner,
      basedOn: medications
        ? MedicationRefillTask.basedOnMedications(medications)
        : undefined,
    })
    this.title = title ?? MedicationRefillTask.defaultTitle
    this.description =
      description ?? MedicationRefillTask.descriptionForCutoff(cutoff)
    this.dueOn = typeof dueOn === 'string' ? dueOn : dueOn.toISOString()

    const participantId = resolveId(participant)
    invariant(
      participantId,
      'MedicationRefillTask requires a participant reference with an id'
    )
    this.addIdentifier(
      MEDICATION_REFILL_TASK_ID_SYSTEM,
      MedicationRefillTask.identifierValueFor(participantId, cutoff)
    )
  }

  static get code(): CodeableConcept {
    return TASK_TYPES.MEDICATION_REFILL
  }

  get medicationRequestRefs(): Reference<MedicationRequest>[] {
    return this.basedOn.filter((ref) => isOfType(ref, 'MedicationRequest'))
  }

  // The `participantId|cutoffDate` key this task is filed under. Public so a
  // bulk read can join tasks back to the drafts that would file them.
  static identifierValueFor(participantId: string, cutoff: Date): string {
    // Formatted in the clinic zone, not the host's: the cutoff instant is 15:00
    // clinic-local, so a host east of UTC+2 would otherwise key the task to the
    // following day and file a duplicate.
    const day = formatInTimeZone(cutoff, DEFAULT_CLINIC_TIME_ZONE, 'yyyy-MM-dd')
    return `${participantId}|${day}`
  }

  get refillKey(): string | undefined {
    return this.identifier(MEDICATION_REFILL_TASK_ID_SYSTEM)?.value
  }

  // One search for many participants, replacing N `byParticipantAndCutoff`
  // probes. Like `byParticipantAndCutoff` it does not filter on state, so a
  // completed task still comes back and still blocks a refile.
  //
  // `authoredAfter` bounds the read to the current cycle: tasks are filed
  // against `nextCutoff`, so one for the cutoff in play cannot predate the
  // cutoff before it. Without the bound this spans every cutoff ever and grows
  // by ~13 tasks per participant per year.
  //
  // Assumes nothing backfills tasks with a backdated `authoredOn`; such a task
  // would fall below the floor and its medications would never merge.
  static byParticipants(
    participants: Reference<Patient>[],
    authoredAfter: Date
  ): QueryTypes {
    return {
      ...MedicationRefillTask.byFocuses(participants),
      'authored-on': `gt${authoredAfter.toISOString()}`,
      _count: '1000',
    }
  }

  // Idempotency criteria — do not use `byFocusString`, which still matches
  // `completed` tasks and would let a second cutoff-day run refile a task that
  // already ran its course.
  static byParticipantAndCutoff(
    participantId: string,
    cutoff: Date
  ): QueryTypes {
    return MedicationRefillTask.byIdentifier(
      MEDICATION_REFILL_TASK_ID_SYSTEM,
      MedicationRefillTask.identifierValueFor(participantId, cutoff)
    )
  }
}
